import { Grid, Box, Button, Typography, Pagination } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useGetOrganizationUserQuery } from '../features/api/rest/apiOrganizationsSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import { useGetOrganizationProductQuery } from '../features/api/rest/apiProductOrganizationSlice';
import ProductOrganizationCard from './ProductOrganizationCard';
import ProductOrganizationDialog from './ProductOrganizationDialog';
import Spinner from './Spinner';
import Error from './Error';

function ProductOrganization({ searchParamsName = '' }) {
  const { opk } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const componentSearchParamsName = searchParamsName ? `${searchParamsName}_` : '';
  const productsForPage = parseInt(searchParams.get(`${componentSearchParamsName}psize`), 10) || 18;
  const productPage = parseInt(searchParams.get(`${componentSearchParamsName}page`), 10) || 1;
  const ordering = searchParams.get(`${componentSearchParamsName}ordering`) || 'name';
  const [isAdding, setIsAdding] = useState(false);
  const { data: profile } = useGetProfileQuery();
  const { data: users = [] } = useGetOrganizationUserQuery(opk);
  const { productList, productsCount, isLoading, isFetching, isError } = useGetOrganizationProductQuery(
    { opk, ordering, page: productPage, psize: productsForPage },
    {
      selectFromResult: (results) => ({
        ...results,
        productList: results.data?.results,
        productsCount: results.data?.count || 0,
      }),
    }
  );
  let SystemAdmin = false;
  let OrgAdmin = false;
  const permission = () => {
    if (profile.is_staff) {
      SystemAdmin = true;
    }
    OrgAdmin = users.some((user) => user.is_admin && profile.id === user.user);
    if (OrgAdmin || SystemAdmin) return true;
    return false;
  };
  const handlePageChange = (page) => {
    searchParams.set(`${componentSearchParamsName}page`, page);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;

  const pagination = <Pagination sx={{ my: 2 }} count={Math.ceil(productsCount / productsForPage)} page={productPage} onChange={(event, newPage) => handlePageChange(newPage)} />;

  return (
    <Box>
      {permission() && (
        <Button
          sx={{ mt: 2 }}
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => {
            setIsAdding(true);
          }}
        >
          {t('organizations.product.add')}
        </Button>
      )}
      {productList && Object.keys(productList).length > 0 ? (
        <>
          {pagination}
          <Grid id="Product_Grid">{productList && productList.map((prod) => <ProductOrganizationCard ean={prod?.ean_code} brands={prod.brands} name={prod.name} key={prod?.ean_code} />)}</Grid>
          {pagination}
        </>
      ) : (
        <Typography variant="h5" sx={{ mt: 3 }}>
          {t('organizations.product.noProducts')}
        </Typography>
      )}
      <ProductOrganizationDialog actionType="add" isOpen={isAdding} setIsOpen={setIsAdding} />
    </Box>
  );
}
export default ProductOrganization;

ProductOrganization.propTypes = {
  searchParamsName: PropTypes.string,
};
